import {
  getLead, updateLead, updateLeadStatus, updateLeadBilling, addLeadInternalNote, sendLeadEmail, archiveLead,
  sendLeadAppointmentConfirmation,
  getFacilityLead, updateFacilityLead, addFacilityNote, sendFacilityEmail, archiveFacilityLead, listFacilityAppointments,
  getContractPatient, updateContractPatient, addContractPatientNote, archiveContractPatient, listPatientAppointments,
  getReferralPartner, updateReferralPartner, addReferralPartnerNote, archiveReferralPartner,
  PAYMENT_STATUSES, PRICING_TYPES, PRICING_MODELS, PAYMENT_TERMS, BILLING_FREQUENCIES,
  canonicalFacilityStatus,
} from "@/lib/api";
import { fmtMoney } from "./clientMeta";

const NAME = { key: "name", label: "Full name", required: true };
const ADDRESS = { key: "address", label: "Address", type: "textarea" };
const REFERRAL = { key: "referral", label: "Referral source", placeholder: "e.g. Dr. Smith · Ocean Primary Care" };
const PHONE = { key: "phone", label: "Phone", type: "tel" };
const EMAIL = { key: "email", label: "Email", type: "email" };
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const optLabel = (opts, v) => opts.find((o) => o.value === v)?.label || v || "—";
const mapJob = (j) => ({
  id: j.id, date: j.preferred_date, time: j.preferred_time, at: j.appointment_at,
  service: (j.services || []).join(", "), status: j.status, address: j.service_address, ref: j.request_id,
});

export const ADAPTERS = {
  consumer: {
    load: getLead,
    view: (r) => ({
      name: r.full_name, email: r.email, phone: r.phone, address: r.service_address, createdAt: r.created_at,
      status: r.status, referral: r.referral_source, services: r.services || [], ref: r.request_id,
      extra: [["Request ID", r.request_id], ["Company", r.company]],
      contacts: [{ name: r.full_name, role: r.company || "Client", phone: r.phone, email: r.email }],
      activity: r.activity_log || [],
    }),
    info: { fields: [NAME, ADDRESS, REFERRAL],
      values: (r) => ({ name: r.full_name, address: r.service_address, referral: r.referral_source }),
      save: (id, v) => updateLead(id, { full_name: v.name, service_address: v.address, referral_source: v.referral }) },
    contact: { fields: [NAME, { ...PHONE, required: true }, EMAIL],
      values: (r) => ({ name: r.full_name, phone: r.phone, email: r.email }),
      save: (id, v) => updateLead(id, { full_name: v.name, phone: v.phone, email: v.email }) },
    saveServices: (id, list) => updateLead(id, { services: list }),
    setStatus: updateLeadStatus,
    addNote: addLeadInternalNote,
    sendEmail: (id, { subject, body }) => sendLeadEmail(id, { subject, body_text: body }),
    sendConfirmation: (id, a) => sendLeadAppointmentConfirmation(id, { date: a.date, time_window: a.time, service: a.service, location: a.location || undefined }),
    archive: archiveLead,
    docEntity: "consumer",
    contractLink: (r) => ({ type: "consumer", id: r.id, label: r.full_name }),
    appointments: async (r) => (r.preferred_date ? [mapJob(r)] : []),
    billing: {
      fields: [
        { key: "amount_due", label: "Amount due ($)", type: "number" },
        { key: "amount_paid", label: "Amount paid ($)", type: "number" },
        { key: "payment_status", label: "Payment status", type: "select", options: PAYMENT_STATUSES },
        { key: "pricing_type", label: "Pricing type", type: "select", options: PRICING_TYPES },
      ],
      values: (r) => ({ amount_due: r.amount_due ?? "", amount_paid: r.amount_paid ?? "", payment_status: r.payment_status, pricing_type: r.pricing_type }),
      summary: (r) => [["Amount due", fmtMoney(r.amount_due)], ["Amount paid", fmtMoney(r.amount_paid)],
        ["Payment status", optLabel(PAYMENT_STATUSES, r.payment_status)], ["Pricing type", r.pricing_type || "—"]],
      save: (id, v) => updateLeadBilling(id, { amount_due: num(v.amount_due), amount_paid: num(v.amount_paid),
        payment_status: v.payment_status || undefined, pricing_type: v.pricing_type || undefined }),
    },
  },

  facility: {
    load: getFacilityLead,
    view: (r) => ({
      name: r.facility_name, email: r.email, phone: r.phone, address: r.address, createdAt: r.created_at,
      status: canonicalFacilityStatus(r.status), referral: r.referral_source,
      services: r.services_of_interest?.length ? r.services_of_interest : (r.service_needed ? [r.service_needed] : []),
      extra: [["Facility type", r.facility_type], ["Coverage", r.coverage_needed]],
      contacts: [
        { name: r.contact_person, role: r.title_role || "Primary contact", phone: r.phone, email: r.email },
        ...(r.accounts_payable_contact || r.billing_email
          ? [{ name: r.accounts_payable_contact || "Accounts payable", role: "Billing contact", email: r.billing_email }] : []),
      ],
      activity: r.activity_log || [],
    }),
    info: { fields: [{ ...NAME, label: "Facility name" }, ADDRESS, { key: "facility_type", label: "Facility type" }, REFERRAL],
      values: (r) => ({ name: r.facility_name, address: r.address, facility_type: r.facility_type, referral: r.referral_source }),
      save: (id, v) => updateFacilityLead(id, { facility_name: v.name, address: v.address || null,
        facility_type: v.facility_type || null, referral_source: v.referral || null }) },
    contact: { fields: [{ ...NAME, label: "Contact name" }, { key: "role", label: "Title / role" }, { ...PHONE, required: true }, { ...EMAIL, required: true }],
      values: (r) => ({ name: r.contact_person, role: r.title_role, phone: r.phone, email: r.email }),
      save: (id, v) => updateFacilityLead(id, { contact_person: v.name, title_role: v.role || null, phone: v.phone, email: v.email }) },
    saveServices: (id, list) => updateFacilityLead(id, { services_of_interest: list }),
    setStatus: (id, s) => updateFacilityLead(id, { status: s }),
    addNote: addFacilityNote,
    sendEmail: (id, { subject, body }) => sendFacilityEmail(id, { action: "follow_up", subject, body_text: body }),
    archive: archiveFacilityLead,
    docEntity: "facility",
    contractLink: (r) => ({ type: "facility", id: r.id, label: r.facility_name }),
    appointments: (r) => listFacilityAppointments(r.id, false).then((d) => (d.appointments || []).map(mapJob)),
    billing: {
      fields: [
        { key: "pricing_model", label: "Pricing model", type: "select", options: PRICING_MODELS },
        { key: "payment_terms", label: "Payment terms", type: "select", options: PAYMENT_TERMS },
        { key: "billing_frequency", label: "Billing frequency", type: "select", options: BILLING_FREQUENCIES },
        { key: "contract_value", label: "Contract value ($)", type: "number" },
        { key: "outstanding_balance", label: "Outstanding balance ($)", type: "number" },
        { key: "last_payment_date", label: "Last payment date", type: "date" },
        { key: "next_invoice_date", label: "Next invoice date", type: "date" },
        { key: "billing_email", label: "Billing email", type: "email" },
      ],
      values: (r) => ({ pricing_model: r.pricing_model, payment_terms: r.payment_terms, billing_frequency: r.billing_frequency,
        contract_value: r.contract_value ?? "", outstanding_balance: r.outstanding_balance ?? "",
        last_payment_date: r.last_payment_date || "", next_invoice_date: r.next_invoice_date || "", billing_email: r.billing_email || "" }),
      summary: (r) => [["Pricing model", optLabel(PRICING_MODELS, r.pricing_model)], ["Payment terms", optLabel(PAYMENT_TERMS, r.payment_terms)],
        ["Billing frequency", optLabel(BILLING_FREQUENCIES, r.billing_frequency)], ["Contract value", fmtMoney(r.contract_value)],
        ["Outstanding balance", fmtMoney(r.outstanding_balance)], ["Last payment", r.last_payment_date || "—"],
        ["Next invoice", r.next_invoice_date || "—"], ["Billing email", r.billing_email || "—"]],
      save: (id, v) => updateFacilityLead(id, {
        pricing_model: v.pricing_model || null, payment_terms: v.payment_terms || null, billing_frequency: v.billing_frequency || null,
        contract_value: num(v.contract_value), outstanding_balance: num(v.outstanding_balance),
        last_payment_date: v.last_payment_date || null, next_invoice_date: v.next_invoice_date || null, billing_email: v.billing_email || null,
      }),
    },
  },

  contract_patient: {
    load: getContractPatient,
    view: (r) => ({
      name: r.full_name, email: r.email, phone: r.phone, address: r.address, createdAt: r.created_at,
      status: r.status, referral: r.referral_source, services: r.authorized_services || [], ref: r.mrn,
      extra: [["Facility", r.facility_name], ["Contract #", r.contract_number], ["MRN", r.mrn]],
      contacts: [
        { name: r.full_name, role: "Patient", phone: r.phone, email: r.email },
        ...(r.primary_facility_contact_name
          ? [{ name: r.primary_facility_contact_name, role: `Facility contact · ${r.facility_name || ""}`, phone: r.primary_facility_contact_phone }] : []),
      ],
      activity: r.activity_log || [],
    }),
    info: { fields: [NAME, ADDRESS, REFERRAL],
      values: (r) => ({ name: r.full_name, address: r.address, referral: r.referral_source }),
      save: (id, v) => updateContractPatient(id, { full_name: v.name, address: v.address || null, referral_source: v.referral || null }) },
    contact: { fields: [NAME, PHONE, EMAIL],
      values: (r) => ({ name: r.full_name, phone: r.phone, email: r.email }),
      save: (id, v) => updateContractPatient(id, { full_name: v.name, phone: v.phone || null, email: v.email || null }) },
    saveServices: (id, list) => updateContractPatient(id, { authorized_services: list }),
    setStatus: (id, s) => updateContractPatient(id, { status: s }),
    addNote: addContractPatientNote,
    sendEmail: null,
    archive: archiveContractPatient,
    docEntity: "contract_patient",
    contractLink: (r) => (r.facility_id ? { type: "facility", id: r.facility_id, label: r.facility_name } : null),
    appointments: (r) => listPatientAppointments(r.id).then((d) => (d.appointments || []).map(mapJob)),
    billing: null,
    billingNote: (r) => `Billed through ${r.facility_name || "the facility"} contract${r.contract_number ? ` #${r.contract_number}` : ""}.`,
  },

  referral_partner: {
    load: getReferralPartner,
    view: (r) => ({
      name: r.name, email: r.email, phone: r.phone, address: r.address, createdAt: r.created_at,
      status: r.status, referral: null, services: r.services || [],
      extra: [["Organization", r.organization], ["Partner type", r.partner_type]],
      contacts: [{ name: r.name, role: r.partner_type || "Referral partner", phone: r.phone, email: r.email }],
      activity: r.activity_log || [],
    }),
    info: { fields: [NAME, { key: "organization", label: "Organization" }, { key: "partner_type", label: "Partner type", placeholder: "Physician, case manager, clinic…" }, ADDRESS],
      values: (r) => ({ name: r.name, organization: r.organization, partner_type: r.partner_type, address: r.address }),
      save: (id, v) => updateReferralPartner(id, { name: v.name, organization: v.organization || null, partner_type: v.partner_type || null, address: v.address || null }) },
    contact: { fields: [NAME, PHONE, EMAIL],
      values: (r) => ({ name: r.name, phone: r.phone, email: r.email }),
      save: (id, v) => updateReferralPartner(id, { name: v.name, phone: v.phone || null, email: v.email || null }) },
    saveServices: (id, list) => updateReferralPartner(id, { services: list }),
    setStatus: (id, s) => updateReferralPartner(id, { status: s }),
    addNote: addReferralPartnerNote,
    sendEmail: null,
    archive: archiveReferralPartner,
    docEntity: null,
    contractLink: () => null,
    appointments: null,
    billing: null,
    billingNote: () => "Referral partners aren't billed.",
  },
};
